<template>
  <div class="chat-ai-empty" data-component-id="chat--empty--root">
    <div class="chat-ai-empty-hero">
      <div class="chat-ai-empty-icon">
        <span class="chat-ai-empty-icon-glow"></span>
        <span class="chat-ai-empty-icon-emoji">💬</span>
      </div>
      <div class="chat-ai-empty-title">Mulai percakapan dengan AI</div>
      <div class="chat-ai-empty-hint">
        Tanyakan apa saja tentang laporan
        <strong>{{ contextLabel }}</strong>
      </div>
    </div>

    <div class="chat-ai-empty-badge" data-component-id="chat--empty--ads-badge">
      <span class="chat-ai-empty-badge-dot"></span>
      <span>🟦 Terhubung ke <strong>Google Ads</strong> — tanya spend, CTR, ROAS real-time</span>
    </div>

    <div class="chat-ai-quick-section">
      <div class="chat-ai-quick-label">
        <span class="chat-ai-quick-dot chat-ai-quick-dot-domain"></span>
        Google Ads
      </div>
      <div class="chat-ai-quick-grid">
        <button
          v-for="(s, i) in adsSuggestions"
          :key="'ads-' + i"
          class="chat-ai-suggestion chat-ai-suggestion-domain"
          :data-component-id="`chat--suggestion--ads-${i}`"
          @click="$emit('select', s)"
        >
          <span class="chat-ai-suggestion-icon">🟦</span>
          <span class="chat-ai-suggestion-text">{{ s }}</span>
          <span class="chat-ai-suggestion-arrow">→</span>
        </button>
      </div>
    </div>

    <div v-if="domainSuggestions.length" class="chat-ai-quick-section">
      <div class="chat-ai-quick-label">
        <span class="chat-ai-quick-dot chat-ai-quick-dot-domain"></span>
        Saran sesuai laporan ini
      </div>
      <div class="chat-ai-quick-grid">
        <button
          v-for="(s, i) in domainSuggestions"
          :key="'dg-' + i"
          class="chat-ai-suggestion chat-ai-suggestion-domain"
          :data-component-id="`chat--suggestion--domain-${i}`"
          @click="$emit('select', s)"
        >
          <span class="chat-ai-suggestion-icon">✨</span>
          <span class="chat-ai-suggestion-text">{{ s }}</span>
          <span class="chat-ai-suggestion-arrow">→</span>
        </button>
      </div>
    </div>

    <div class="chat-ai-quick-section">
      <div class="chat-ai-quick-label">
        <span class="chat-ai-quick-dot"></span>
        Saran cepat
      </div>
      <div class="chat-ai-quick-grid">
        <button
          v-for="(s, i) in baseSuggestions"
          :key="'sg-' + i"
          class="chat-ai-suggestion"
          :data-component-id="`chat--suggestion--base-${i}`"
          @click="$emit('select', s)"
        >
          <span class="chat-ai-suggestion-icon">💡</span>
          <span class="chat-ai-suggestion-text">{{ s }}</span>
          <span class="chat-ai-suggestion-arrow">→</span>
        </button>
      </div>
    </div>

    <div class="chat-ai-empty-foot">
      <kbd>Enter</kbd> kirim · <kbd>Shift</kbd>+<kbd>Enter</kbd> baris baru · <kbd>?</kbd> shortcut
    </div>
  </div>
</template>

<script setup>
import { GOOGLE_ADS_SUGGESTIONS as adsSuggestions } from '../data/chatSuggestions'

defineProps({
  contextLabel:      { type: String, default: '' },
  baseSuggestions:   { type: Array,  default: () => [] },
  domainSuggestions: { type: Array,  default: () => [] },
})
defineEmits(['select'])
</script>

<style scoped>
.chat-ai-empty {
  display: flex;
  flex-direction: column;
  align-items: stretch;
  text-align: center;
  padding: 28px 16px 16px;
  gap: 18px;
  margin: auto;
  width: 100%;
  max-width: 640px;
}

.chat-ai-empty-hero {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
}

.chat-ai-empty-icon {
  position: relative;
  width: 64px;
  height: 64px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  background: linear-gradient(
    135deg,
    var(--accent-soft, rgba(59, 130, 246, 0.16)),
    rgba(139, 92, 246, 0.18)
  );
  border: 1px solid var(--accent, var(--accent-blue, #3b82f6));
  margin-bottom: 4px;
  animation: chat-ai-float 3.6s ease-in-out infinite;
}
.chat-ai-empty-icon-glow {
  position: absolute;
  inset: -6px;
  border-radius: 50%;
  background: radial-gradient(
    circle,
    var(--accent-soft, rgba(59, 130, 246, 0.25)) 0%,
    transparent 70%
  );
  filter: blur(8px);
  animation: chat-ai-pulse 2.4s ease-in-out infinite;
  z-index: 0;
}
.chat-ai-empty-icon-emoji {
  position: relative;
  z-index: 1;
  font-size: 28px;
}
@keyframes chat-ai-float {
  0%, 100% { transform: translateY(0); }
  50%      { transform: translateY(-6px); }
}
@keyframes chat-ai-pulse {
  0%, 100% { opacity: 0.6; transform: scale(1); }
  50%      { opacity: 1;   transform: scale(1.08); }
}

.chat-ai-empty-title {
  font-size: 16px;
  font-weight: 600;
  letter-spacing: -0.2px;
  color: var(--text-primary, var(--text-body));
}
.chat-ai-empty-hint {
  font-size: 12.5px;
  color: var(--text-muted, var(--text-ghost, #888));
  line-height: 1.45;
}
.chat-ai-empty-hint strong {
  color: var(--accent-blue-light, var(--accent, #3b82f6));
  font-weight: 600;
}

.chat-ai-empty-badge {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  align-self: center;
  padding: 6px 12px;
  border-radius: 999px;
  font-size: 11.5px;
  color: var(--text-base, var(--text-body));
  background: linear-gradient(
    135deg,
    var(--accent-soft, rgba(59, 130, 246, 0.16)),
    color-mix(in srgb, var(--accent-purple, #8b5cf6) 10%, transparent)
  );
  border: 1px solid color-mix(in srgb, var(--accent, #3b82f6) 35%, transparent);
}
.chat-ai-empty-badge strong { color: var(--accent-blue-light, var(--accent, #3b82f6)); font-weight: 700; }
.chat-ai-empty-badge-dot {
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background: var(--accent-green, #22c55e);
  box-shadow: 0 0 8px var(--accent-green, #22c55e);
  animation: chat-ai-pulse 2.4s ease-in-out infinite;
  flex-shrink: 0;
}

.chat-ai-quick-section {
  display: flex;
  flex-direction: column;
  gap: 8px;
  text-align: left;
}
.chat-ai-quick-label {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 10.5px;
  color: var(--text-muted, var(--text-ghost, #888));
  text-transform: uppercase;
  letter-spacing: 0.6px;
  font-weight: 600;
}
.chat-ai-quick-dot {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: var(--text-dim, #6b7a8d);
  display: inline-block;
}
.chat-ai-quick-dot-domain {
  background: var(--accent, var(--accent-blue, #3b82f6));
  box-shadow: 0 0 6px var(--accent-soft, rgba(59, 130, 246, 0.5));
}

.chat-ai-quick-grid {
  display: grid;
  grid-template-columns: 1fr;
  gap: 6px;
}
@media (min-width: 560px) {
  .chat-ai-quick-grid { grid-template-columns: 1fr 1fr; }
}

.chat-ai-suggestion {
  display: flex;
  align-items: center;
  gap: 8px;
  background: var(--bg-elev, var(--bg-card));
  border: 1px solid var(--border-soft, var(--border-light));
  color: var(--text-base, var(--text-body));
  padding: 9px 12px;
  border-radius: 8px;
  cursor: pointer;
  font-size: 12px;
  text-align: left;
  line-height: 1.4;
  transition: background 0.18s, border-color 0.18s, transform 0.08s, box-shadow 0.18s;
  position: relative;
  overflow: hidden;
}
.chat-ai-suggestion::before {
  content: '';
  position: absolute;
  left: 0;
  top: 0;
  bottom: 0;
  width: 2px;
  background: transparent;
  transition: background 0.18s;
}
.chat-ai-suggestion:hover {
  background: var(--bg-hover, rgba(125, 180, 245, 0.04));
  border-color: var(--accent, var(--accent-blue, #3b82f6));
  transform: translateY(-1px);
  box-shadow: 0 4px 12px -4px var(--accent-soft, rgba(59, 130, 246, 0.25));
}
.chat-ai-suggestion:hover::before {
  background: var(--accent, var(--accent-blue, #3b82f6));
}
.chat-ai-suggestion:active {
  transform: scale(0.985);
}
.chat-ai-suggestion-icon {
  flex-shrink: 0;
  font-size: 13px;
  opacity: 0.85;
}
.chat-ai-suggestion-text {
  flex: 1;
  white-space: normal;
}
.chat-ai-suggestion-arrow {
  flex-shrink: 0;
  font-size: 12px;
  color: var(--text-dim, #6b7a8d);
  opacity: 0;
  transform: translateX(-4px);
  transition: opacity 0.15s, transform 0.15s, color 0.15s;
}
.chat-ai-suggestion:hover .chat-ai-suggestion-arrow {
  opacity: 1;
  transform: translateX(0);
  color: var(--accent, var(--accent-blue, #3b82f6));
}
.chat-ai-suggestion-domain {
  border-style: solid;
  border-color: var(--accent-soft, rgba(59, 130, 246, 0.35));
  background: linear-gradient(
    135deg,
    var(--bg-elev, var(--bg-card)) 60%,
    var(--accent-soft, rgba(59, 130, 246, 0.07))
  );
}

.chat-ai-empty-foot {
  margin-top: 4px;
  text-align: center;
  font-size: 10.5px;
  color: var(--text-dim, #6b7a8d);
  letter-spacing: 0.2px;
}
.chat-ai-empty-foot kbd {
  display: inline-block;
  padding: 1px 5px;
  border: 1px solid var(--border-soft, var(--border-light));
  border-radius: 3px;
  font-family: 'Consolas', 'Monaco', monospace;
  font-size: 10px;
  background: var(--bg-elev, var(--bg-card));
  color: var(--text-base, var(--text-body));
  margin: 0 1px;
}
</style>
